import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { ShareSociety } from "./ShareSociety";
import { trackLead } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

export const Waitlist = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [position, setPosition] = useState(null);
  const [count, setCount] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const fetchCount = () =>
      axios.get(`${API}/waitlist/count`).then(({ data }) => {
        if (active) setCount(data.count);
      }).catch(() => {});
    fetchCount();
    const id = setInterval(fetchCount, 15000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email: email.trim() });
      trackLead();
      setPosition(data.position || null);
      setCount((c) => (c === null ? c : c + 1));
      setJoined(true);
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      if (err?.response?.status === 409) {
        setJoined(true);
      } else {
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="join" data-testid="waitlist" className="relative py-28 md:py-44 border-t border-bds-border">
      <div className="mx-auto max-w-[1100px] px-6 md:px-12">
        <Reveal>
          <span className="font-mono-bds text-xs uppercase tracking-[0.3em] text-bds-accent">
            The Society
          </span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-serif-display font-light text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] mt-6 mb-10">
            Enter the <span className="italic">Society</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-grotesk text-base md:text-lg text-bds-muted max-w-xl leading-relaxed mb-6">
            Chapter I arrives 26 November.<br />Members hear first.
          </p>
        </Reveal>
        {count !== null && count > 0 && (
          <Reveal delay={0.12}>
            <p data-testid="waitlist-live-count" className="font-mono-bds text-[11px] uppercase tracking-[0.3em] text-bds-muted mb-14">
              Join <span className="text-bds-fg/85 tabular-nums">{count.toLocaleString()}</span> others in the Society
            </p>
          </Reveal>
        )}

        {joined ? (
          <Reveal>
            <div data-testid="waitlist-success" role="status" className="max-w-2xl">
              <p className="font-serif-display text-4xl md:text-5xl tracking-tight">You&apos;re in.</p>
              {position && (
                <p data-testid="waitlist-position" className="font-serif-display italic text-2xl md:text-3xl text-bds-muted mt-4">
                  You are the {ordinal(position)} to enter.
                </p>
              )}
              <p className="font-mono-bds text-[11px] uppercase tracking-[0.25em] text-bds-muted mt-5">
                We&apos;ll contact you when it&apos;s time.
              </p>
            </div>
            <div className="max-w-2xl mt-10">
              <ShareSociety title="Bring someone with you" subtitle="Send this to someone who needs it too." />
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="waitlist-form" className="max-w-2xl">
              <div className="flex flex-col sm:flex-row gap-8 sm:gap-6 sm:items-end">
                <div className="flex-1">
                  <label htmlFor="society-email" className="font-mono-bds text-[10px] uppercase tracking-[0.3em] text-bds-muted block mb-3">
                    Email address
                  </label>
                  <input
                    id="society-email"
                    data-testid="waitlist-email-input"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    aria-describedby={error ? "waitlist-error" : undefined}
                    className="w-full bg-transparent border-b border-bds-border focus:border-bds-fg outline-none py-4 font-grotesk text-lg placeholder:text-bds-muted/60 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="waitlist-submit-btn"
                  className="font-mono-bds text-xs uppercase tracking-[0.25em] px-14 py-5 bg-bds-fg text-bds-bg hover:bg-bds-accent hover:text-bds-cream transition-colors duration-300 disabled:opacity-50 self-start sm:self-auto"
                >
                  {loading ? "…" : "Enter"}
                </button>
              </div>
              {error && (
                <p id="waitlist-error" data-testid="waitlist-error" role="alert" className="font-mono-bds text-[11px] uppercase tracking-[0.2em] text-bds-accent mt-4">
                  {error}
                </p>
              )}
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
};
